import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  DatePicker,
  InputNumber,
  Tag,
  Progress,
  Spin,
  Empty,
  Statistic,
} from 'antd';
import { Target, Plus, Edit2, Trash2 } from 'lucide-react';
import dayjs from 'dayjs';
import useApiMethods from '../../lib/useApiMethods';
import { TARGETS, TARGETS_VS_ACTUAL } from '../../constants/api';
import ToastMsg from '../../components/common/ToastMsg';

const formatINR = (amount) => {
  const num = Number(amount);
  if (isNaN(num)) return amount;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(num);
};

const METRIC_OPTIONS = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'deals_closed', label: 'Deals Closed' },
  { value: 'pipeline_value', label: 'Pipeline Value' },
  { value: 'new_leads', label: 'New Leads' },
  { value: 'csat_score', label: 'CSAT Score' },
  { value: 'ticket_resolution_hours', label: 'Avg Ticket Resolution (hrs)' },
  { value: 'headcount', label: 'Headcount' },
  { value: 'budget_utilization', label: 'Budget Utilization %' },
  { value: 'expenses', label: 'Total Expenses' },
  { value: 'net_profit', label: 'Net Profit' },
];

const DEPARTMENT_OPTIONS = [
  { value: 'company', label: 'Company-wide' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'hr', label: 'HR' },
  { value: 'sales', label: 'Sales' },
  { value: 'support', label: 'Support' },
  { value: 'finance', label: 'Finance' },
  { value: 'operations', label: 'Operations' },
];

const PERIOD_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const METRIC_LABELS = Object.fromEntries(METRIC_OPTIONS.map((m) => [m.value, m.label]));
const DEPARTMENT_LABELS = Object.fromEntries(DEPARTMENT_OPTIONS.map((d) => [d.value, d.label]));
const PERIOD_LABELS = Object.fromEntries(PERIOD_OPTIONS.map((p) => [p.value, p.label]));

// Metrics that display as currency
const CURRENCY_METRICS = new Set(['revenue', 'pipeline_value', 'expenses', 'net_profit']);

const statusConfig = {
  on_track: { color: 'green', label: 'On Track' },
  at_risk: { color: 'orange', label: 'At Risk' },
  behind: { color: 'red', label: 'Behind' },
};

const Targets = () => {
  const { fetch, create, update, remove } = useApiMethods();
  const [targets, setTargets] = useState([]);
  const [vsActual, setVsActual] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vsActualLoading, setVsActualLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [form] = Form.useForm();

  // Current month start for vs-actual
  const currentMonthStart = dayjs().startOf('month').format('YYYY-MM-DD');

  const loadTargets = useCallback(async () => {
    const res = await fetch(TARGETS, {}, setLoading);
    if (res?.status !== 'fail') {
      setTargets(res?.data?.results || []);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVsActual = useCallback(async () => {
    const res = await fetch(
      TARGETS_VS_ACTUAL,
      { period_start: currentMonthStart, period: 'monthly' },
      setVsActualLoading
    );
    if (res?.status !== 'fail') {
      setVsActual(res?.data || []);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonthStart]);

  useEffect(() => {
    loadTargets();
    loadVsActual();
  }, [loadTargets, loadVsActual]);

  const handleSave = async (values) => {
    const payload = {
      metric: values.metric,
      department: values.department,
      period: values.period,
      period_start: values.period_start.format('YYYY-MM-DD'),
      target_value: values.target_value,
    };

    let res;
    if (editingTarget) {
      res = await update(`${TARGETS}${editingTarget.id}/`, payload);
    } else {
      res = await create(TARGETS, payload);
    }

    if (res?.status !== 'fail') {
      ToastMsg('success', editingTarget ? 'Target updated' : 'Target created');
      setModalOpen(false);
      setEditingTarget(null);
      form.resetFields();
      loadTargets();
      loadVsActual();
    } else {
      ToastMsg('error', res?.message || 'Failed to save target');
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Delete Target',
      content: 'Are you sure you want to delete this target?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        const res = await remove(`${TARGETS}${id}/`);
        if (res?.status !== 'fail') {
          ToastMsg('success', 'Target deleted');
          loadTargets();
          loadVsActual();
        }
      },
    });
  };

  const openEdit = (target) => {
    setEditingTarget(target);
    form.setFieldsValue({
      metric: target.metric,
      department: target.department,
      period: target.period,
      period_start: dayjs(target.period_start),
      target_value: Number(target.target_value),
    });
    setModalOpen(true);
  };

  const openCreate = () => {
    setEditingTarget(null);
    form.resetFields();
    form.setFieldsValue({
      department: 'company',
      period: 'monthly',
      period_start: dayjs().startOf('month'),
    });
    setModalOpen(true);
  };

  const formatValue = (metric, value) => {
    const num = Number(value);
    if (isNaN(num)) return value;
    if (CURRENCY_METRICS.has(metric)) return formatINR(num);
    if (metric === 'budget_utilization') return `${num}%`;
    if (metric === 'csat_score') return `${num}/5`;
    if (metric === 'ticket_resolution_hours') return `${num} hrs`;
    return num.toLocaleString('en-IN');
  };

  // Find vs-actual data for a target row
  const findVsActual = (target) => {
    return vsActual.find(
      (va) =>
        va.metric === target.metric &&
        va.department === target.department &&
        va.period_start === target.period_start
    );
  };

  const columns = [
    {
      title: 'Metric',
      dataIndex: 'metric',
      key: 'metric',
      render: (val) => <span className="font-medium">{METRIC_LABELS[val] || val}</span>,
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (val) => DEPARTMENT_LABELS[val] || val,
    },
    {
      title: 'Period',
      dataIndex: 'period',
      key: 'period',
      render: (val) => PERIOD_LABELS[val] || val,
    },
    {
      title: 'Period Start',
      dataIndex: 'period_start',
      key: 'period_start',
      render: (val) => dayjs(val).format('MMM YYYY'),
    },
    {
      title: 'Target',
      dataIndex: 'target_value',
      key: 'target_value',
      render: (val, record) => formatValue(record.metric, val),
    },
    {
      title: 'Actual',
      key: 'actual',
      render: (_, record) => {
        const va = findVsActual(record);
        if (!va) return <span className="text-muted-foreground">-</span>;
        return formatValue(record.metric, va.actual_value);
      },
    },
    {
      title: '%',
      key: 'percentage',
      width: 80,
      render: (_, record) => {
        const va = findVsActual(record);
        if (!va) return <span className="text-muted-foreground">-</span>;
        return `${va.percentage}%`;
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => {
        const va = findVsActual(record);
        if (!va) return <span className="text-muted-foreground">-</span>;
        const config = statusConfig[va.status] || statusConfig.behind;
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            type="text"
            size="small"
            icon={<Edit2 size={14} />}
            onClick={() => openEdit(record)}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<Trash2 size={14} />}
            onClick={() => handleDelete(record.id)}
          />
        </div>
      ),
    },
  ];

  // Key metrics for the summary cards (from vs-actual data)
  const keyMetrics = ['revenue', 'deals_closed', 'pipeline_value', 'csat_score'];
  const summaryCards = keyMetrics
    .map((metric) => vsActual.find((va) => va.metric === metric))
    .filter(Boolean);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Targets & Goals</h2>
          <p className="text-muted-foreground text-sm">
            Set monthly, quarterly, and yearly targets per department
          </p>
        </div>
        <Button
          type="primary"
          icon={<Plus size={16} />}
          onClick={openCreate}
          style={{ backgroundColor: '#4f46e5' }}
        >
          Add Target
        </Button>
      </div>

      {/* Section 1: Target vs Actual Summary Cards */}
      {vsActualLoading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : summaryCards.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {summaryCards.map((va) => {
            const config = statusConfig[va.status] || statusConfig.behind;
            const progressColor =
              va.status === 'on_track'
                ? '#10b981'
                : va.status === 'at_risk'
                ? '#f59e0b'
                : '#ef4444';

            return (
              <Card key={va.metric} className="shadow-sm">
                <div className="mb-3">
                  <span className="text-muted-foreground text-sm">{METRIC_LABELS[va.metric]}</span>
                  <Tag color={config.color} className="ml-2 text-xs">
                    {config.label}
                  </Tag>
                </div>
                <Statistic
                  value={Number(va.actual_value)}
                  prefix={CURRENCY_METRICS.has(va.metric) ? '\u20B9' : undefined}
                  suffix={va.metric === 'csat_score' ? '/5' : undefined}
                  precision={CURRENCY_METRICS.has(va.metric) ? 0 : va.metric === 'csat_score' ? 1 : 0}
                  valueStyle={{ fontSize: '1.5rem', fontWeight: 600 }}
                />
                <div className="mt-2 text-xs text-muted-foreground">
                  of {formatValue(va.metric, va.target_value)} target
                </div>
                <Progress
                  percent={Math.min(va.percentage, 100)}
                  showInfo={false}
                  strokeColor={progressColor}
                  size="small"
                  className="mt-2"
                />
                <div className="text-right text-xs text-muted-foreground mt-1">{va.percentage}%</div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="shadow-sm">
          <Empty
            description="No target vs actual data for this month. Add targets to see comparisons."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}

      {/* Section 2: All Targets Table */}
      <Card
        title={
          <span className="flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-500" />
            All Targets
          </span>
        }
        className="shadow-sm rounded-xl"
      >
        <Table
          dataSource={targets}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 15 }}
          size="middle"
          locale={{
            emptyText: (
              <Empty description="No targets set yet. Click 'Add Target' to get started." />
            ),
          }}
        />
      </Card>

      {/* Add/Edit Target Modal */}
      <Modal
        title={editingTarget ? 'Edit Target' : 'Add Target'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditingTarget(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText={editingTarget ? 'Update' : 'Create'}
        okButtonProps={{ style: { backgroundColor: '#4f46e5' } }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="metric"
            label="Metric"
            rules={[{ required: true, message: 'Select a metric' }]}
          >
            <Select options={METRIC_OPTIONS} placeholder="Select metric" />
          </Form.Item>

          <Form.Item
            name="department"
            label="Department"
            rules={[{ required: true, message: 'Select a department' }]}
          >
            <Select options={DEPARTMENT_OPTIONS} placeholder="Select department" />
          </Form.Item>

          <Form.Item
            name="period"
            label="Period"
            rules={[{ required: true, message: 'Select a period' }]}
          >
            <Select options={PERIOD_OPTIONS} placeholder="Select period" />
          </Form.Item>

          <Form.Item
            name="period_start"
            label="Period Start"
            rules={[{ required: true, message: 'Select a start date' }]}
          >
            <DatePicker
              picker="month"
              className="w-full"
              format="MMM YYYY"
            />
          </Form.Item>

          <Form.Item
            name="target_value"
            label="Target Value"
            rules={[{ required: true, message: 'Enter the target value' }]}
          >
            <InputNumber
              className="w-full"
              min={0}
              precision={2}
              placeholder="e.g. 2000000"
              formatter={(value) =>
                value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
              }
              parser={(value) => value?.replace(/,/g, '')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Targets;
